/**
 * PublicFormPage — the standalone, unauthenticated capture page at /f/:slug.
 * Renders a board form's fields, submits to the public endpoint, and shows a
 * thank-you state. Manus-minimal; no app chrome. A hidden honeypot field traps
 * bots (submitted but silently dropped server-side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formsApi, type PublicForm, type PublicFormAttribution } from '../../services/api/formsApi';
import { readAttributionFromUrl } from './publicFormAttribution';

function Field({
  field,
  value,
  onChange,
}: {
  field: PublicForm['fields'][number];
  value: unknown;
  onChange: (v: unknown) => void;
}): React.ReactElement {
  const base =
    'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-900 focus:outline-none';
  // `key` is the only field identity the public payload carries — the server
  // never sends columnId across this boundary. See formsApi.PublicFormField.
  const common = { id: field.key, name: field.key, required: field.required };

  if (field.type === 'LONG_TEXT') {
    return (
      <textarea
        {...common}
        className={base}
        rows={4}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === 'CHECKBOX') {
    return (
      <input
        {...common}
        type="checkbox"
        className="mt-2 h-4 w-4 rounded border-gray-300"
        checked={value === true}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }
  const inputType =
    field.type === 'EMAIL' ? 'email' : field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : 'text';
  return (
    <input
      {...common}
      type={inputType}
      className={base}
      value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function PublicFormPage(): React.ReactElement {
  const { slug = '' } = useParams();
  const [form, setForm] = useState<PublicForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // Read once, on the first render, from the URL the visitor actually arrived
  // on — see publicFormAttribution.ts. A lazy initialiser, not an effect: the
  // value must exist before any submit can happen, and must never be re-read.
  const [attribution] = useState<PublicFormAttribution | undefined>(() =>
    readAttributionFromUrl(window.location.search, window.location.href, document.referrer),
  );

  useEffect(() => {
    let active = true;
    formsApi
      .getPublic(slug)
      .then((res) => {
        if (active) setForm(res.data);
      })
      .catch((e) => {
        if (active) setLoadError(e instanceof Error ? e.message : 'This form is unavailable.');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setSubmitError(null);
      try {
        await formsApi.submit(slug, values, attribution);
        setDone(true);
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Could not submit. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [slug, values, attribution],
  );

  const set = (fieldKey: string, v: unknown): void => setValues((prev) => ({ ...prev, [fieldKey]: v }));

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        {loadError ? (
          <p className="text-center text-sm text-gray-500">{loadError}</p>
        ) : !form ? (
          <p className="text-center text-sm text-gray-400">Loading…</p>
        ) : done ? (
          <div className="py-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900">Thank you</h1>
            <p className="mt-2 text-sm text-gray-500">Your response has been recorded.</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 className="text-2xl font-bold text-gray-900">{form.title}</h1>
            {form.description && <p className="mt-2 text-sm text-gray-500">{form.description}</p>}

            <div className="mt-6 space-y-4">
              {form.fields.map((field) => (
                <div key={field.key}>
                  <label htmlFor={field.key} className="text-sm font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </label>
                  <Field field={field} value={values[field.key]} onChange={(v) => set(field.key, v)} />
                </div>
              ))}
            </div>

            {/* Honeypot — hidden from humans; bots that fill it are dropped. */}
            <input
              type="text"
              name={form.honeypotField}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
              value={typeof values[form.honeypotField] === 'string' ? (values[form.honeypotField] as string) : ''}
              onChange={(e) => set(form.honeypotField, e.target.value)}
            />

            {submitError && <p className="mt-4 text-sm text-amber-700">{submitError}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
